import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';
import { fuseAnimations } from '@fuse/animations';
import { MessagesService } from 'app/layout/common/messages/messages.service';
import { Message, ActiveToast, ToastPriority } from 'app/layout/common/messages/messages.types';

const MAX_VISIBLE_TOASTS = 5;

@Component({
    selector: 'toast-container',
    standalone: true,
    imports: [NgClass, MatButtonModule, MatIconModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: fuseAnimations,
    template: `
        <div class="fixed top-4 right-4 z-[9999] flex flex-col items-end gap-3 pointer-events-none max-w-md w-full">
            @for (toast of sortedToasts; track toast.message.id) {
                <div
                    class="pointer-events-auto w-full rounded-lg border shadow-lg p-4 flex items-start gap-3"
                    [ngClass]="getToastClasses(toast.priority)"
                    @slideInRight
                >
                    <!-- Icon -->
                    @if (toast.message.icon) {
                        <mat-icon
                            class="icon-size-5 flex-shrink-0 mt-0.5"
                            [svgIcon]="toast.message.icon">
                        </mat-icon>
                    }

                    <!-- Content -->
                    <div class="flex-1 min-w-0">
                        @if (toast.message.title) {
                            <div class="font-semibold text-sm truncate">{{ toast.message.title }}</div>
                        }
                        @if (toast.message.description) {
                            <div class="text-sm opacity-90 line-clamp-2 mt-0.5">{{ toast.message.description }}</div>
                        }
                    </div>

                    <!-- Actions -->
                    <div class="flex items-center gap-1 flex-shrink-0">
                        @if (toast.message.link) {
                            <button
                                mat-icon-button
                                class="h-7 min-h-7 w-7"
                                (click)="viewToast(toast)">
                                <mat-icon class="icon-size-4" svgIcon="heroicons_outline:arrow-top-right-on-square"></mat-icon>
                            </button>
                        }
                        <button
                            mat-icon-button
                            class="h-7 min-h-7 w-7"
                            (click)="dismissToast(toast)">
                            <mat-icon class="icon-size-4" svgIcon="heroicons_solid:x-mark"></mat-icon>
                        </button>
                    </div>
                </div>
            }
        </div>
    `,
})
export class ToastContainerComponent implements OnInit, OnDestroy {
    private _unsubscribeAll = new Subject<void>();
    private _toasts: ActiveToast[] = [];

    get sortedToasts(): ActiveToast[] {
        return [...this._toasts].sort((a, b) => {
            if (a.priority !== b.priority) return a.priority - b.priority;
            return a.createdAt - b.createdAt;
        });
    }

    constructor(
        private _messagesService: MessagesService,
        private _cdr: ChangeDetectorRef,
        private _router: Router,
    ) {}

    ngOnInit(): void {
        this._messagesService.newMessage$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(msg => this._handleNewMessage(msg));
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        // Clear all timers
        this._toasts.forEach(t => { if (t.timerId) clearTimeout(t.timerId); });
    }

    dismissToast(toast: ActiveToast): void {
        this._removeToast(toast);
        // Manual dismiss = mark as read
        this._messagesService.markAsReadViaSocket(toast.message.id);
        this._cdr.markForCheck();
    }

    viewToast(toast: ActiveToast): void {
        if (toast.message.link) {
            if (toast.message.useRouter) {
                this._router.navigateByUrl(toast.message.link);
            } else {
                this._router.navigateByUrl(toast.message.link);
            }
        }
        this.dismissToast(toast);
    }

    getToastClasses(priority: ToastPriority): Record<string, boolean> {
        return {
            'bg-red-50 border-red-300 text-red-900 dark:bg-red-900/80 dark:border-red-700 dark:text-red-100': priority === ToastPriority.ADMIN_PERSISTENT,
            'bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-900/80 dark:border-amber-700 dark:text-amber-100': priority === ToastPriority.ADMIN_TIMED,
            'bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-900/80 dark:border-blue-700 dark:text-blue-100': priority === ToastPriority.EVENT,
        };
    }

    // ---

    private _handleNewMessage(msg: Message): void {
        const priority = this._calcPriority(msg);

        // Replacement: same entity_id replaces existing toast
        if (msg.entity_id) {
            const existing = this._toasts.find(
                t => t.message.entity_id === msg.entity_id && t.message.entity_type === msg.entity_type
            );
            if (existing) {
                if (existing.timerId) clearTimeout(existing.timerId);
                existing.message = msg;
                existing.priority = priority;
                existing.createdAt = Date.now();
                this._scheduleAutoDismiss(existing);
                this._cdr.markForCheck();
                return;
            }
        }

        // Evict oldest EVENT toast if at limit
        if (this._toasts.length >= MAX_VISIBLE_TOASTS) {
            const eventToasts = this._toasts
                .filter(t => t.priority === ToastPriority.EVENT)
                .sort((a, b) => a.createdAt - b.createdAt);
            if (eventToasts.length > 0) {
                this._removeToast(eventToasts[0]);
            } else {
                // All are admin toasts, evict oldest
                const oldest = [...this._toasts].sort((a, b) => a.createdAt - b.createdAt)[0];
                if (oldest) this._removeToast(oldest);
            }
        }

        const toast: ActiveToast = {
            message: msg,
            priority,
            createdAt: Date.now(),
        };
        this._toasts.push(toast);
        this._scheduleAutoDismiss(toast);
        this._cdr.markForCheck();
    }

    private _calcPriority(msg: Message): ToastPriority {
        if (msg.source_type === 'admin') {
            return msg.is_persistent ? ToastPriority.ADMIN_PERSISTENT : ToastPriority.ADMIN_TIMED;
        }
        return ToastPriority.EVENT;
    }

    private _scheduleAutoDismiss(toast: ActiveToast): void {
        // Persistent admin toasts don't auto-dismiss
        if (toast.priority === ToastPriority.ADMIN_PERSISTENT) return;

        const timeout = toast.message.timeout_ms || 10000;
        toast.timerId = setTimeout(() => {
            this._removeToast(toast);
            // Auto-dismiss does NOT mark as read
            this._cdr.markForCheck();
        }, timeout);
    }

    private _removeToast(toast: ActiveToast): void {
        if (toast.timerId) clearTimeout(toast.timerId);
        const idx = this._toasts.indexOf(toast);
        if (idx > -1) this._toasts.splice(idx, 1);
    }
}
